import { useEffect, useState } from "react";

import { Alert, type Flash } from "../components/Alert";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { Card, Empty } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Checkbox, Field, Input, Textarea } from "../components/Field";
import { Modal, ModalForm } from "../components/Modal";
import { PageHead } from "../components/PageHead";
import {
  CELL_ACTIONS,
  CELL_MUTED,
  CELL_STRONG,
  MONO,
  Table,
  TableScroll,
  Tbody,
  Td,
  Thead,
} from "../components/Table";
import { createSite, deleteSite, fetchSites, updateSite } from "../lib/api";
import { messageOf } from "../lib/errors";
import { useAction } from "../lib/useAction";
import {
  OIDC_OPTIONAL_SCOPES,
  firstError,
  required,
  validateEntryURL,
  validateRedirectURIs,
  validateSiteCode,
} from "../lib/validate";
import type { CreateSiteInput, Session, Site } from "../lib/types";

/**
 * 서비스 카탈로그 관리 (`/sites`).
 *
 * 갤러리에 보이는 타일의 정본을 편집합니다. `plt:site:manage` 없이는 이 화면에 들어올 수 없습니다
 * (App 이 갤러리로 돌려보냅니다 — 서버도 403 으로 거부합니다).
 */
export function SitesScreen({
  session,
  revision,
  onFlash,
  onRefresh,
}: {
  session: Session;
  revision: number;
  onFlash: (flash: Flash) => void;
  onRefresh: () => void;
}) {
  const [sites, setSites] = useState<Site[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** null = 닫힘, "new" = 추가, Site = 수정. 세 상태를 한 변수로 두면 모달이 둘 열릴 수 없습니다. */
  const [form, setForm] = useState<Site | "new" | null>(null);
  const [deleting, setDeleting] = useState<Site | null>(null);

  useEffect(() => {
    let alive = true;
    fetchSites()
      .then((rows) => {
        if (!alive) return;
        setSites(rows);
        setLoadError(null);
      })
      .catch((err: unknown) => {
        if (alive) setLoadError(messageOf(err));
      });
    return () => {
      alive = false;
    };
  }, [revision]);

  /** 실패는 그대로 던집니다 — 폼이 받아 창 안에 보여주고 입력을 유지합니다. 성공일 때만 닫습니다. */
  const onSubmit = async (target: Site | "new", input: CreateSiteInput) => {
    if (target === "new") await createSite(input, session.csrfToken);
    else await updateSite(target.siteId, input, session.csrfToken);
    onFlash({
      kind: "ok",
      text: target === "new" ? "서비스를 추가했습니다." : "서비스를 저장했습니다.",
    });
    setForm(null);
    onRefresh();
  };

  const onDelete = async (site: Site) => {
    await deleteSite(site.siteId, session.csrfToken);
    onFlash({ kind: "ok", text: `${site.displayName} 서비스를 삭제했습니다.` });
    onRefresh();
  };

  return (
    <>
      <PageHead
        title="카탈로그"
        sub="갤러리에 보이는 서비스 타일을 관리합니다."
        actions={<Button onClick={() => setForm("new")}>서비스 추가</Button>}
      />

      {loadError ? <Alert kind="error">{loadError}</Alert> : null}

      <Card>
        {sites.length === 0 && !loadError ? <Empty>등록된 서비스가 없습니다.</Empty> : null}
        {sites.length > 0 ? (
          <TableScroll>
            <Table>
              <Thead columns={["서비스", "코드", "주소", "로그인", "정렬", ""]} />
              <Tbody>
                {sites.map((s) => (
                  <tr key={s.siteId}>
                    <Td>
                      <div className={CELL_STRONG}>
                        {s.icon ? (
                          // 이모지는 글리프 폭이 제각각이라 공백 문자 하나로는 간격이 일정하지 않습니다.
                          <span aria-hidden="true" className="mr-[6px] inline-block">
                            {s.icon}
                          </span>
                        ) : null}
                        {s.displayName}
                      </div>
                      <div className={CELL_MUTED}>{s.description ?? ""}</div>
                    </Td>
                    <Td>
                      <span className={MONO}>{s.siteCode}</span>
                    </Td>
                    <Td>
                      <span className={MONO}>{s.entryUrl}</span>
                    </Td>
                    <Td>
                      {s.ssoEnabled ? (
                        <Badge tone="brand">통합 로그인</Badge>
                      ) : (
                        <Badge tone="muted">개별</Badge>
                      )}{" "}
                      {s.oidcRedirectUris?.length ? <Badge tone="brand">OIDC</Badge> : null}
                    </Td>
                    <Td>
                      <span className={CELL_MUTED}>{String(s.sortOrder)}</span>
                    </Td>
                    <Td>
                      <div className={CELL_ACTIONS}>
                        <Button variant="ghost" size="sm" onClick={() => setForm(s)}>
                          수정
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => setDeleting(s)}>
                          삭제
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </Tbody>
            </Table>
          </TableScroll>
        ) : null}
      </Card>

      {form ? (
        <SiteForm
          target={form}
          onClose={() => setForm(null)}
          onSubmit={(input) => onSubmit(form, input)}
        />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          title="서비스 삭제"
          message={`${deleting.displayName}(${deleting.siteCode}) 를 갤러리에서 삭제합니다. 구성원이 더 이상 이 타일을 볼 수 없습니다.`}
          confirmLabel="삭제"
          onConfirm={() => onDelete(deleting)}
          onClose={() => setDeleting(null)}
        />
      ) : null}
    </>
  );
}

/** 기본 정렬 순서 — 서버도 같은 값을 쓰지만, 숫자 입력이 비었을 때의 폴백으로 명시합니다. */
const DEFAULT_SORT_ORDER = 1000;

function SiteForm({
  target,
  onClose,
  onSubmit,
}: {
  target: Site | "new";
  onClose: () => void;
  /** reject 되면 메시지를 창 안에 보여주고 창을 유지합니다. */
  onSubmit: (input: CreateSiteInput) => Promise<unknown>;
}) {
  const action = useAction();
  const [touched, setTouched] = useState(false);
  const isNew = target === "new";
  const site = isNew ? null : target;

  const [siteCode, setSiteCode] = useState(site?.siteCode ?? "");
  const [displayName, setDisplayName] = useState(site?.displayName ?? "");
  const [description, setDescription] = useState(site?.description ?? "");
  // 추가 폼은 `https://` 로 시작해 둡니다 — 서버가 https 절대 URL 만 받습니다.
  const [entryUrl, setEntryUrl] = useState(site?.entryUrl ?? "https://");
  const [icon, setIcon] = useState(site?.icon ?? "");
  const [sortOrder, setSortOrder] = useState(String(site?.sortOrder ?? DEFAULT_SORT_ORDER));
  const [ssoEnabled, setSsoEnabled] = useState(Boolean(site?.ssoEnabled));
  const [redirectText, setRedirectText] = useState((site?.oidcRedirectUris ?? []).join("\n"));
  const [scopes, setScopes] = useState(site?.oidcScopes ?? []);

  const redirectUris = redirectText
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const toggleScope = (scope: string, on: boolean) =>
    setScopes((prev) => (on ? [...prev, scope] : prev.filter((x) => x !== scope)));

  const order = sortOrder.trim();
  const invalid = firstError(
    validateSiteCode(siteCode.trim()),
    required(displayName.trim(), "표시 이름을 입력하세요."),
    validateEntryURL(entryUrl.trim()),
    order === "" || Number.isInteger(Number(order)) ? null : "정렬 순서는 정수여야 합니다.",
    validateRedirectURIs(redirectUris),
  );

  const submit = () => {
    setTouched(true);
    if (invalid) return;
    void action.run(() =>
      onSubmit({
        siteCode: siteCode.trim(),
        displayName: displayName.trim(),
        // 빈 문자열이 아니라 null 로 보냅니다 — 서버의 nullable 컬럼에 "" 를 남기면 갤러리가
        // 빈 설명 줄을 렌더하고, 값이 없다는 사실이 DB 에서 사라집니다.
        description: description.trim() || null,
        entryUrl: entryUrl.trim(),
        icon: icon.trim() || null,
        ssoEnabled,
        sortOrder: order === "" ? DEFAULT_SORT_ORDER : Number(order),
        oidcRedirectUris: redirectUris,
        oidcScopes: redirectUris.length
          ? ["openid", ...OIDC_OPTIONAL_SCOPES.filter((x) => scopes.includes(x))]
          : [],
      }),
    );
  };

  /** 응답 대기 중에는 닫지 않습니다 — 요청이 나간 뒤 창만 사라지면 저장 여부를 알 수 없습니다. */
  const close = () => {
    if (!action.pending) onClose();
  };

  return (
    <Modal
      title={isNew ? "서비스 추가" : "서비스 수정"}
      sub="코드는 서비스 계정의 소속 시스템(owning_system)과 같은 값이어야 통합 로그인이 위임됩니다."
      onClose={close}
      footer={
        <>
          <Button variant="ghost" disabled={action.pending} onClick={close}>
            취소
          </Button>
          <Button pending={action.pending} onClick={submit}>
            {isNew ? "추가" : "저장"}
          </Button>
        </>
      }
      status={
        action.error ? (
          <Alert kind="error">{`${isNew ? "추가" : "저장"} 실패: ${action.error}`}</Alert>
        ) : touched && invalid ? (
          <Alert kind="warn">{invalid}</Alert>
        ) : null
      }
    >
      <ModalForm>
        <Field id="s-code" label="서비스 코드">
          <Input
            id="s-code"
            value={siteCode}
            maxLength={20}
            placeholder="예: rn"
            onChange={(ev) => setSiteCode(ev.target.value)}
          />
        </Field>
        <Field id="s-name" label="표시 이름">
          <Input
            id="s-name"
            value={displayName}
            placeholder="예: 연구노트 관리"
            onChange={(ev) => setDisplayName(ev.target.value)}
          />
        </Field>
        <Field id="s-desc" label="설명">
          <Textarea
            id="s-desc"
            value={description}
            placeholder="갤러리 타일에 보일 한두 줄 설명"
            onChange={(ev) => setDescription(ev.target.value)}
          />
        </Field>
        <Field id="s-url" label="진입 주소 (https)">
          <Input
            id="s-url"
            value={entryUrl}
            placeholder="https://rn.postdemy.com"
            onChange={(ev) => setEntryUrl(ev.target.value)}
          />
        </Field>
        <Field id="s-icon" label="아이콘">
          <Input
            id="s-icon"
            value={icon}
            maxLength={8}
            placeholder="이모지 1자"
            onChange={(ev) => setIcon(ev.target.value)}
          />
        </Field>
        <Field id="s-order" label="정렬 순서">
          <Input
            id="s-order"
            type="number"
            value={sortOrder}
            onChange={(ev) => setSortOrder(ev.target.value)}
          />
        </Field>
        <Checkbox
          id="s-sso"
          label="통합 로그인 위임 대상"
          checked={ssoEnabled}
          onChange={setSsoEnabled}
        />
        <Field id="s-oidc" label="OIDC 돌려보낼 주소 (한 줄에 하나)">
          <Textarea
            id="s-oidc"
            value={redirectText}
            placeholder="비워 두면 OIDC 를 쓰지 않습니다"
            onChange={(ev) => setRedirectText(ev.target.value)}
          />
        </Field>
        {redirectUris.length ? (
          <Field id="s-scope" label="받아 갈 수 있는 정보 (openid 는 항상 포함)">
            {OIDC_OPTIONAL_SCOPES.map((scope) => (
              <Checkbox
                key={scope}
                id={`s-scope-${scope}`}
                label={scope}
                checked={scopes.includes(scope)}
                onChange={(on) => toggleScope(scope, on)}
              />
            ))}
          </Field>
        ) : null}
      </ModalForm>
    </Modal>
  );
}
